import { NavLink } from 'react-router-dom';

import { cn, haptic } from '@/lib/utils';
import { useVoteWindowStore } from '@/stores/voteWindowStore';

/**
 * House-drawn glyphs, 24px viewBox, single stroke-based `<path>` per icon —
 * no icon library. Each renders as an outline (inactive) or a filled
 * silhouette (active, orange) via the shared `NavIcon` wrapper below.
 */
const icons = {
  home: 'M12 3L21 10V21H3V10L12 3Z',
  vote: 'M4 8H20L18 21H6L4 8Z M10 12H14V14H10Z',
  credits: 'M4 6H17L20 9V15L17 18H4Z M14 6V18',
  profile: 'M12 4a4 4 0 100 8 4 4 0 000-8Z M4 20c0-4.4 3.6-8 8-8s8 3.6 8 8v1H4v-1Z',
} as const;

function NavIcon({ d, active }: { d: string; active: boolean }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      aria-hidden
      fill={active ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.8}
      strokeLinejoin="round"
      strokeLinecap="round"
      fillRule="evenodd"
    >
      <path d={d} />
    </svg>
  );
}

const tabs = [
  { to: '/', label: 'Accueil', icon: icons.home },
  { to: '/vote', label: 'Votes', icon: icons.vote },
  { to: '/credits', label: 'Crédits', icon: icons.credits },
  { to: '/profile', label: 'Profil', icon: icons.profile },
];

/**
 * Floating glass tab bar. The active tab gets the orange accent; we use
 * NavLink so the route drives the visual state (no React state to sync).
 */
export function BottomNav() {
  // Fenêtre de vote ouverte → pastille sur l'onglet Votes (design-handoff
  // item 8, report chantier 5). État purement affiché : `isOpen` vient de
  // l'API via voteWindowStore, ce composant ne décide RIEN métier — pas de
  // badge tant que le store n'a jamais fetché avec succès (`isOpen === null`).
  const voteWindowOpen = useVoteWindowStore((s) => s.isOpen);

  return (
    <nav
      className="fixed inset-x-0 bottom-3 z-40 mx-auto w-[calc(100%-24px)] max-w-phone rounded-[28px] bg-paper/95 px-2.5 py-2 shadow-[0_8px_30px_rgba(60,40,20,0.18)] backdrop-blur-md dark:bg-d-bg/85"
      aria-label="Navigation principale"
    >
      <div className="flex justify-around">
        {tabs.map((t) => {
          const showVoteBadge = t.to === '/vote' && voteWindowOpen === true;
          return (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.to === '/'}
              onClick={() => haptic(8)}
              aria-label={showVoteBadge ? `${t.label}, fenêtre ouverte` : undefined}
              className={({ isActive }) =>
                cn(
                  'flex min-w-[60px] flex-col items-center gap-0.5 rounded-2xl px-2.5 py-2 font-medium transition-colors',
                  isActive ? 'text-orange font-bold' : 'text-muted dark:text-d-muted',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className="relative flex h-7 w-7 items-center justify-center rounded-xl transition-colors"
                    aria-hidden
                  >
                    <NavIcon d={t.icon} active={isActive} />
                    {showVoteBadge && (
                      // Contraste non-texte (WCAG 1.4.11, ≥3:1) : bg-orange sur
                      // bg-paper ne rendait que 2,72:1 en light — terracotta sur
                      // paper porte à ~5,2:1 (aplat déjà kit, cf. ADMIN_COLORS/
                      // pickColor) ; en dark, orange sur d-bg reste largement
                      // au-dessus du seuil (fond quasi noir), donc inchangé.
                      <span className="absolute right-0.5 top-0.5 h-2 w-2 rounded-full bg-terracotta ring-2 ring-paper dark:bg-orange dark:ring-d-bg" />
                    )}
                  </span>
                  <span className="text-[10px] tracking-wide">{t.label}</span>
                </>
              )}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
